import { HoleWinnerEnum } from '../__generated__/hooks'

/**
 * Running standing after each hole of a match, from what each hole moved it.
 *
 * Swings rather than winners: a Best Worst hole is worth two points, and a side
 * leading on the tee takes one at most, so counting holes won would misstate
 * the match. The server applies that cap; summing its swings is all that is
 * left here. `holeSwings` stops at the deciding hole, like `holes`.
 */
export const runningStanding = (swings: readonly number[]): string[] => {
  let net = 0
  return swings.map((swing) => {
    net += swing
    if (net === 0) return 'AS'
    return `${Math.abs(net)} ${net > 0 ? 'UP' : 'DN'}`
  })
}

/**
 * A decided result in words. A multi-point lead also names the holes that
 * were left: "5 UP" with two to play is decided only because four points
 * could not close it, and "5 & 2" would read as singles notation.
 */
export const decidedMargin = (state: {
  margin?: string | null
  pointsPerHole: number
  holesRemaining: number
}): string => {
  const margin = state.margin ?? ''
  return state.pointsPerHole > 1 && state.holesRemaining > 0
    ? `${margin} · ${state.holesRemaining} hull igjen`
    : margin
}

/** Best Worst's two legs, in the order the server sends them. */
export const LEG_LABELS = ['Beste', 'Dårligste'] as const

/**
 * The holes of a round in the order this flight plays them.
 *
 * Mirrors `inPlayOrder` on the API, which is what `state.holes` is indexed by:
 * a shotgun flight starting on 7 plays 7…18 then 1…6, so the nth entry of
 * `holes` is the nth hole THEY played, not hole n.
 */
export const playOrderNumbers = (
  holes: readonly { number: number }[],
  startHoleNumber?: number | null
): number[] => {
  const sorted = [...holes].map((h) => h.number).sort((a, b) => a - b)
  if (startHoleNumber == null) return sorted
  const start = sorted.indexOf(startHoleNumber)
  if (start <= 0) return sorted
  return [...sorted.slice(start), ...sorted.slice(0, start)]
}

/** Who leads, and by how much, after the first `count` holes played. */
export const standingAfter = (
  swings: readonly number[],
  count: number
): { leader: 'A' | 'B' | null; holesUp: number } => {
  const net = swings.slice(0, count).reduce((sum, swing) => sum + swing, 0)
  if (net > 0) return { leader: 'A', holesUp: net }
  if (net < 0) return { leader: 'B', holesUp: -net }
  return { leader: null, holesUp: 0 }
}

/**
 * What just happened on the hole in front of the player.
 *
 * The aggregate standing alone is not feedback: a match that comes back to
 * level reads exactly like a match where nothing registered. Both really
 * happen, and the player has no way to tell them apart — which is precisely
 * the confusion this exists to remove.
 *
 * `legs` carries each leg's own winner (one leg outside Best Worst), and
 * `capped` says the leader won more legs than the hole paid them.
 *
 * `null` when the hole has not resolved yet (one or both sides still to score);
 * the caller decides whether that is worth saying.
 */
export const holeOutcome = (
  state: {
    holes: readonly HoleWinnerEnum[]
    holeLegs: readonly (readonly HoleWinnerEnum[])[]
    holeSwings: readonly number[]
  },
  order: readonly number[],
  currentHoleNumber: number
): {
  winner: HoleWinnerEnum
  legs: readonly HoleWinnerEnum[]
  capped: boolean
  leader: 'A' | 'B' | null
  holesUp: number
} | null => {
  const index = order.indexOf(currentHoleNumber)
  if (index < 0 || index >= state.holes.length) return null
  const legs = state.holeLegs[index] ?? []
  const swing = state.holeSwings[index] ?? 0
  const raw = legs.reduce(
    (sum, w) =>
      sum + (w === HoleWinnerEnum.A ? 1 : w === HoleWinnerEnum.B ? -1 : 0),
    0
  )
  return {
    winner: state.holes[index],
    legs,
    capped: Math.abs(raw) > Math.abs(swing),
    ...standingAfter(state.holeSwings, index + 1),
  }
}
